'use client'

import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { noop } from 'lodash'

import { UiState } from '@marketplace-web/shared/ui-state-data'
import { isResponseError } from '@marketplace-web/shared/api-client'

import { getCategoryAttributes } from 'data/api/get-category-attributes'
import { ItemAttributeModel } from 'types/models/item-edit'

import { DynamicAttribute } from '../types'
import { DynamicAttributesContextType } from '../types/context'
import useFieldErrors from '../hooks/useFieldErrors'
import { useAttributesContext } from './AttributesContext'
import { FieldName } from '../constants'
import { transformDynamicAttributesToModel } from '../transformers/dynamic-attribute'
import { DynamicAttributeModel, AdditionalAttributeModel } from '../types/dynamic-attribute'

export const DynamicAttributesContext = createContext<DynamicAttributesContextType>({
  uiState: UiState.Idle,
  selectedDynamicAttributes: [],
  fetchCategoryAttributes: () => Promise.resolve(),
  getDynamicAttributeByCode: () => undefined,
  getAdditionalAttributesByCode: () => undefined,
  setSelectedDynamicAttributeValues: noop,
  getSelectedDynamicAttributeValue: () => [],
  setSelectedDynamicAttributeValue: noop,
})

export const DynamicAttributesContextProvider = ({ children }: { children: ReactNode }) => {
  const { setCatalogId } = useAttributesContext()

  const [dynamicAttributes, setDynamicAttributes] = useState<{
    byName: { [fieldName: string]: DynamicAttributeModel }
  }>({
    byName: {},
  })
  const [additionalAttributes, setAdditionalAttributes] = useState<{
    byName: { [fieldName: string]: AdditionalAttributeModel }
  }>({
    byName: {},
  })
  const [catalogAttributes, setCatalogAttributes] = useState<{
    byName: { [fieldName: string]: Array<number> }
    names: Array<string>
  }>({ byName: {}, names: [] })
  const [uiState, setUiState] = useState<UiState>(UiState.Idle)

  const { removeFieldError } = useFieldErrors()

  const fetchCategoryAttributes = useCallback(
    async (catalogId: number | null) => {
      if (!catalogId) return

      setUiState(UiState.Pending)
      const response = await getCategoryAttributes(catalogId)

      if (isResponseError(response) || !response.attributes) {
        setCatalogId(null)
        setUiState(UiState.Failure)

        return
      }

      setDynamicAttributes({
        byName: transformDynamicAttributesToModel(response.attributes).reduce(
          (acc, attr) => ({
            ...acc,
            [attr.code]: attr,
          }),
          {},
        ),
      })

      setAdditionalAttributes({
        byName: response.additional_attributes.reduce(
          (acc, attr) => ({
            ...acc,
            [attr.code]: attr,
          }),
          {},
        ),
      })

      setUiState(UiState.Success)
    },
    [setCatalogId],
  )

  const setSelectedDynamicAttributeValues = useCallback(
    (selectedValues: Array<ItemAttributeModel> | undefined) => {
      if (!selectedValues) {
        setCatalogAttributes({ byName: {}, names: [] })

        return
      }

      const byName = selectedValues.reduce(
        (acc, { code, ids }) => ({
          ...acc,
          [code]: ids,
        }),
        {},
      )
      const names = selectedValues.map(({ code }) => code)

      setCatalogAttributes({ byName, names })
    },
    [],
  )

  const selectedDynamicAttributes = useMemo(
    () =>
      catalogAttributes.names
        .map(name => {
          if (!dynamicAttributes.byName[name]) return null

          return {
            field: name,
            value: catalogAttributes.byName[name],
          }
        })
        .filter((attribute): attribute is DynamicAttribute => !!attribute),
    [dynamicAttributes.byName, catalogAttributes.names, catalogAttributes.byName],
  )

  const getDynamicAttributeByCode = useCallback(
    (fieldName: string) => dynamicAttributes.byName[fieldName] || undefined,
    [dynamicAttributes.byName],
  )

  const getAdditionalAttributesByCode = useCallback(
    (fieldName: string) => additionalAttributes.byName[fieldName] || undefined,
    [additionalAttributes.byName],
  )

  const getSelectedDynamicAttributeValue = useCallback(
    (fieldName: string) => catalogAttributes.byName[fieldName] || [],
    [catalogAttributes.byName],
  )

  const setSelectedDynamicAttributeValue = useCallback(
    (field: string, value: Array<number>) => {
      setCatalogAttributes(prev => {
        const updatedByName = { ...prev.byName, [field]: value }
        const updatedNames = prev.names.includes(field) ? prev.names : [...prev.names, field]

        return {
          byName: updatedByName,
          names: updatedNames,
        }
      })

      removeFieldError(field as FieldName)
    },
    [setCatalogAttributes, removeFieldError],
  )

  const providerValue = useMemo(
    () => ({
      uiState,
      selectedDynamicAttributes,
      fetchCategoryAttributes,
      getDynamicAttributeByCode,
      getAdditionalAttributesByCode,
      setSelectedDynamicAttributeValues,
      getSelectedDynamicAttributeValue,
      setSelectedDynamicAttributeValue,
    }),
    [
      uiState,
      selectedDynamicAttributes,
      fetchCategoryAttributes,
      getDynamicAttributeByCode,
      getAdditionalAttributesByCode,
      setSelectedDynamicAttributeValues,
      getSelectedDynamicAttributeValue,
      setSelectedDynamicAttributeValue,
    ],
  )

  return (
    <DynamicAttributesContext.Provider value={providerValue}>
      {children}
    </DynamicAttributesContext.Provider>
  )
}

export const useDynamicAttributesContext = () => {
  const context = useContext(DynamicAttributesContext)

  if (!context) {
    throw new Error(
      'useDynamicAttributesContext must be used within a DynamicAttributesContextProvider',
    )
  }

  return context
}
